/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import find from 'lodash/find';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import { Dispatch } from 'redux';
import { internalAuthCampingAxios } from 'ui-internal-camping/src/utils/internalCampingAxios';

import type { Selection } from '../../../types/camping';
import {
    CAMPSITE_AVAILABILITY_DESELECTED_CARD,
    CAMPSITE_AVAILABILITY_SELECTED_CARD,
    CLEAR_GET_AVAILABLE_CAMPSITES_ERROR,
    CLEAR_LTS_CAMPSITE,
    CLEAR_SELECTIONS,
    CLOSE_ACCESSIBILITY_MODAL,
    CLOSE_CAMPGROUND_FILTERS_MODAL,
    CLOSE_ITINERARY_DETAILS_MODAL,
    CLOSE_RENTAL_MODAL,
    CONTINUE_LOADING,
    FETCH_CAMPSITES_AVAILABILITY,
    FETCH_CAMPSITES_AVAILABILITY_LOADING,
    INIT_CAMPGROUND_AVAILABILITY_STATES,
    MAP_CAMPSITE_IDS_DESELECTED,
    MAP_CAMPSITE_IDS_SELECTED,
    RESET_MAP_VIEW,
    OPEN_ACCESSIBILITY_MODAL,
    OPEN_CAMPGROUND_FILTERS_MODAL,
    OPEN_ITINERARY_DETAILS_MODAL,
    RESERVATION_DETAILS_TOGGLE,
    SET_FILTER_INPUT,
    SET_GRID_RANGE,
    SET_GRID_SIZE,
    SET_SORT_PROPERTY,
    TOGGLE_FILTER_AS_YOU_MOVE,
    UPDATE_SELECTIONS,
    UPDATE_SELECTION_BY_ID,
    UPDATE_SELECTION_WITH_RES_ID,
} from '../constants';
import { campingAxios } from '../utils/campingAxios';
import { APIType } from './types';

export const json = (response: { data: any }) => response.data;

export const initCampgroundAvailabilityStates = ({
    salesChannel,
}: {
    salesChannel: string;
}) => {
    return (dispatch: Dispatch<any>) => {
        dispatch({
            type: INIT_CAMPGROUND_AVAILABILITY_STATES,
            payload: {
                salesChannel,
            },
        });
    };
};

export const clearCampsiteAvailabilityError = () => {
    return (dispatch: Dispatch<any>) => {
        dispatch({ type: CLEAR_GET_AVAILABLE_CAMPSITES_ERROR });
        return Promise.resolve();
    };
};

export const updateSelections = ({
    campsiteId,
    date,
    facilityType,
    name = '',
    loop = '',
    isInternal = false,
    isTransfer = false,
    canModifyInflightReservation,
    isLTSSelection,
    isBooking = false,
}: {
    campsiteId: string;
    date: string;
    facilityType?: string;
    name?: string;
    loop?: string;
    isInternal?: boolean;
    isTransfer?: boolean;
    canModifyInflightReservation?: boolean;
    isLTSSelection?: boolean;
    isBooking?: boolean;
}) => {
    return (dispatch: Dispatch<any>) => {
        dispatch({
            type: UPDATE_SELECTIONS,
            payload: {
                campsiteId,
                date,
                facilityType,
                name,
                loop,
                isInternal,
                isTransfer,
                canModifyInflightReservation,
                isLTSSelection,
                isBooking,
            },
        });
    };
};

export const updateSelectionsById = ({
    campsiteId,
    selection,
}: {
    campsiteId: string;
    selection: Selection;
}) => {
    return (dispatch: Dispatch<any>) => {
        dispatch({
            type: UPDATE_SELECTION_BY_ID,
            payload: {
                campsiteId,
                selection,
            },
        });
    };
};

// VOYA-specific action, since there could be potential boat rentals made that needs associated reservation id
export const updateSelectionWithReservationId = ({
    campsiteId,
    reservationId,
}: {
    campsiteId: string;
    reservationId: string;
}) => {
    return (dispatch: Dispatch<any>) => {
        dispatch({
            type: UPDATE_SELECTION_WITH_RES_ID,
            payload: {
                campsiteId,
                reservationId,
            },
        });
    };
};

export const clearSelections = () => {
    return (dispatch: Dispatch<any>) => {
        dispatch({
            type: CLEAR_SELECTIONS,
        });
    };
};

export const setFilterText = (filterText: string) => {
    return (dispatch: Dispatch<any>) => {
        dispatch({
            type: SET_FILTER_INPUT,
            payload: filterText,
        });
    };
};

export const setGridRange = ({
    startDate,
    endDate,
}: {
    startDate: string;
    endDate: string;
}) => {
    return (dispatch: Dispatch<any>) => {
        dispatch({
            type: SET_GRID_RANGE,
            payload: { startDate, endDate },
        });
    };
};

export const setContinueLoading = () => {
    return (dispatch: Dispatch<any>) => {
        dispatch({
            type: CONTINUE_LOADING,
        });
    };
};

export const setSortProperty = (columnId: string, isAsc: boolean) => {
    return (dispatch: Dispatch<any>) => {
        dispatch({
            type: SET_SORT_PROPERTY,
            payload: {
                columnId,
                isAsc,
            },
        });
    };
};
export const closeRentalModal = () => {
    return (dispatch: Dispatch<any>) => {
        dispatch({ type: CLOSE_RENTAL_MODAL });
    };
};

export const openCampgroundFiltersModal = () => {
    return { type: OPEN_CAMPGROUND_FILTERS_MODAL };
};

export const closeCampgroundFiltersModal = () => {
    return { type: CLOSE_CAMPGROUND_FILTERS_MODAL };
};

export const openAccessibilityModal = () => {
    return { type: OPEN_ACCESSIBILITY_MODAL };
};

export const closeAccessibilityModal = () => {
    return { type: CLOSE_ACCESSIBILITY_MODAL };
};

export const openItineraryDetailsModal = () => {
    return { type: OPEN_ITINERARY_DETAILS_MODAL };
};

export const closeItineraryDetailsModal = () => {
    return { type: CLOSE_ITINERARY_DETAILS_MODAL };
};

export const setGridSize = (gridSize: number) => {
    return { type: SET_GRID_SIZE, gridSize };
};

export const toggleReservationDetailsValue = () => {
    return { type: RESERVATION_DETAILS_TOGGLE };
};

export const setMapCampsiteIdSelected = (featureId: string) => {
    return { type: CAMPSITE_AVAILABILITY_SELECTED_CARD, payload: featureId };
};

export const removeMapCampsiteIdSelected = () => {
    return { type: CAMPSITE_AVAILABILITY_DESELECTED_CARD };
};

export const setMapCampsiteIdsFilter = (campsiteIds: string[]) => {
    return { type: MAP_CAMPSITE_IDS_SELECTED, payload: campsiteIds };
};

export const removeMapCampsiteIdsFilter = () => {
    return { type: MAP_CAMPSITE_IDS_DESELECTED };
};

export const resetMapView = () => {
    return { type: RESET_MAP_VIEW };
};

export const toggleFilterAsYouMoveValue = (forceValue?: boolean) => {
    return { type: TOGGLE_FILTER_AS_YOU_MOVE, payload: forceValue };
};

export const fetchCampsitesAvailabilityWorker =
    (campsiteIds: string[], apiType: APIType) => async (dispatch: Dispatch<any>) => {
        const localAxios =
            apiType === APIType.Internal ? internalAuthCampingAxios : campingAxios;

        dispatch({
            type: FETCH_CAMPSITES_AVAILABILITY_LOADING,
        });

        try {
            const results = await Promise.all(
                map(campsiteIds, (campsiteId) =>
                    localAxios.get(`/availability/campsite/${campsiteId}/all`)
                )
            );

            const campsitesAvailabilities = map(results, (result) =>
                get(result, 'data.availability', null)
            );
            const availabilities: { [key: string]: { campsite_id: string } } = {};
            forEach(campsiteIds, (campsiteId) => {
                const campsiteAvailability = find(
                    campsitesAvailabilities,
                    (availability) => availability.campsite_id === campsiteId
                );
                if (!isNil(campsiteAvailability)) {
                    availabilities[campsiteId] = campsiteAvailability;
                }
            });

            dispatch({
                type: FETCH_CAMPSITES_AVAILABILITY,
                payload: availabilities,
            });
        } catch {
            // no-op
        }
    };

export const fetchCampsitesAvailability =
    (campsiteIds: string[]) => async (dispatch: Dispatch<any>) =>
        fetchCampsitesAvailabilityWorker(campsiteIds, APIType.External)(dispatch);

export const clearLTSCampsite = () => {
    return { type: CLEAR_LTS_CAMPSITE };
};
